import { collection, doc, getDoc, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "./init";
import { UserWithRole } from "@/types/iam";

/**
 * 從 Firestore 獲取所有 IAM 用戶（admin、staff、developer 和 observer）
 */
export async function getIAMUsers(): Promise<UserWithRole[]> {
  try {
    const iamCollection = collection(db, "iam");
    const q = query(iamCollection, orderBy("updatedAt", "desc"));
    const querySnapshot = await getDocs(q);

    const users: UserWithRole[] = [];
    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      users.push({
        id: docSnapshot.id,
        uid: data.uid,
        email: data.email || null,
        displayName: data.displayName || null,
        photoURL: data.photoURL || null,
        isAdmin: data.admin === true,
        isStaff: data.staff === true || data.admin === true,
        isDeveloper: data.developer === true,
        isObserver: data.observer === true,
        createdAt: data.updatedAt?.toDate(),
      });
    });

    return users;
  } catch (error) {
    console.error("Error getting IAM users:", error);
    throw error;
  }
}

/**
 * 獲取單個用戶的 IAM 信息
 */
export async function getIAMUser(uid: string): Promise<UserWithRole | null> {
  try {
    const iamDoc = await getDoc(doc(db, "iam", uid));
    if (!iamDoc.exists()) {
      return null;
    }

    const data = iamDoc.data();
    return {
      id: iamDoc.id,
      uid: data.uid,
      email: data.email || null,
      displayName: data.displayName || null,
      photoURL: data.photoURL || null,
      isAdmin: data.admin === true,
      isStaff: data.staff === true || data.admin === true,
      isDeveloper: data.developer === true,
      isObserver: data.observer === true,
      createdAt: data.updatedAt?.toDate(),
    };
  } catch (error) {
    console.error("Error getting IAM user:", error);
    throw error;
  }
}

/**
 * 通過 email 搜索用戶（用於新增功能）
 * 注意：這需要用戶已經在 Firebase Auth 中註冊
 */
export async function searchUserByEmail(email: string): Promise<{
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
} | null> {
  try {
    // 注意：Firebase Auth 沒有直接的 email 搜索 API
    // 我們需要通過 Functions 來實現，或者使用其他方法
    // 這裡先返回 null，實際搜索將通過 Functions 實現
    return null;
  } catch (error) {
    console.error("Error searching user by email:", error);
    throw error;
  }
}

